// client/src/components/ScreensaverField.jsx
// Kiosk screensaver uploader (ADR-019) — shown inside DeviceForm when the
// blocked display is IMAGE. Accepts an image or a PDF, opens CropModal so the
// manager fits it to the portrait kiosk screen, and uploads ONLY the cropped
// 1200×1920 JPEG. Persists immediately (like the camp LogoField) — not gated
// on the form's Save button — so it needs an existing device id.

import { useRef, useState } from 'react';
import CropModal from './CropModal.jsx';
import {
  useUploadDeviceScreensaver,
  useDeleteDeviceScreensaver,
} from '../hooks/useDevices.js';

// The original may be heavy (posters, PDFs) — it never uploads, so the cap is
// generous; only the ~0.5MB crop result goes to the server.
const MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;

export default function ScreensaverField({ campId, device }) {
  const inputRef = useRef(null);
  const upload = useUploadDeviceScreensaver(campId);
  const remove = useDeleteDeviceScreensaver(campId);
  const [cropFile, setCropFile] = useState(null);
  const [error, setError] = useState(null);
  // The `device` prop is a snapshot taken when the edit form opened — the
  // devices-list refetch doesn't reach it. After our own upload/removal we
  // override what the prop says so the preview tile updates immediately.
  const [override, setOverride] = useState(null); // null | { has: boolean, v: number }

  const hasScreensaver = override ? override.has : !!device?.kioskScreensaverMime;
  const version = override?.v || Date.parse(device?.kioskScreensaverUpdatedAt || '') || 0;

  function pick(e) {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    const isPdf = file.type === 'application/pdf';
    if (!isPdf && !file.type.startsWith('image/')) {
      setError('יש לבחור קובץ תמונה או PDF.');
      return;
    }
    if (file.size > MAX_ORIGINAL_BYTES) {
      setError('הקובץ גדול מדי (עד 25MB).');
      return;
    }
    setCropFile(file);
  }

  async function onCropped(blob) {
    try {
      await upload.mutateAsync({ deviceId: device.id, blob });
      setOverride({ has: true, v: Date.now() });
      setCropFile(null);
    } catch (err) {
      setCropFile(null);
      setError(
        err?.code === 'not_a_kiosk' ? 'שומר מסך זמין רק לעמדת שירות עצמי.' :
        err?.status === 413 ? 'התמונה החתוכה גדולה מדי. נסו קובץ אחר.' :
        'ההעלאה נכשלה. נסו שוב.'
      );
    }
  }

  async function onRemove() {
    setError(null);
    try {
      await remove.mutateAsync(device.id);
      setOverride({ has: false, v: 0 });
    } catch {
      setError('ההסרה נכשלה. נסו שוב.');
    }
  }

  return (
    <div className="pt-1">
      <div className="flex items-start gap-3">
        {/* Portrait preview tile — same 10:16 aspect as the kiosk panel. */}
        <div className="w-20 shrink-0 aspect-[10/16] rounded-lg border border-ink-200 bg-ink-900 overflow-hidden grid place-items-center">
          {hasScreensaver ? (
            <img
              src={`/api/camps/${campId}/devices/${device.id}/screensaver?v=${version}`}
              alt="שומר מסך"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-[10px] text-ink-400 text-center px-1">מסך שחור (אין תמונה)</span>
          )}
        </div>
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'מעלה...' : hasScreensaver ? 'החלפת תמונה' : 'העלאת תמונה'}
            </button>
            {hasScreensaver && (
              <button
                type="button"
                className="btn-ghost btn-sm text-joy-700"
                onClick={onRemove}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'מסיר...' : 'הסרה'}
              </button>
            )}
          </div>
          <p className="hint">
            תמונה או PDF (עד 25MB). הקובץ ייחתך ליחס המסך של העמדה — לגובה —
            ויישמר חתוך בלבד. השמירה מיידית.
          </p>
          {error && <p className="text-xs text-joy-700 font-semibold">{error}</p>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="sr-only"
        onChange={pick}
        aria-label="בחירת קובץ שומר מסך"
      />
      {cropFile && (
        <CropModal file={cropFile} onCancel={() => setCropFile(null)} onCropped={onCropped} />
      )}
    </div>
  );
}
